/** Strict browser-side contract for a reviewed Workflow Checker campaign. */

import { getAction } from '../registry';
import { signRecord } from '../decision-records/signature';
import { indexToken } from './workspaces/token';
import {
    deriveWorkflowStepBudget,
    DEFAULT_DOCK_POSE_UPPER_BOUND,
} from './budget/workflowStepBudget';

export const WORKFLOW_SUBMISSION_VERSION = 1;
export const WORKFLOW_SUMMARY_MAX_LENGTH = 320;

/**
 * Tags a refusal that is about the SHAPE of the workspace rather than the budget,
 * so the panel can explain it as such and say what to change. Each one names a
 * distinct thing the reviewer has to fix, because "multiple targets" and "no
 * target" call for opposite actions.
 */
export const MULTI_PROTEIN_WORKSPACE = 'multi-protein-workspace';
export const NO_TARGET_PROTEIN = 'no-target-protein';
export const UNATTRIBUTED_COMPOUND = 'unattributed-compound';

// The reviewed pose count a campaign will sign. The contract pins the value the
// shortcut *proposes*, not the one the reviewer may approve — reviewing is the
// point of the Workflow Checker, and the step budget derives from whatever they
// approve.
//
// The minimum is inherent: fewer than one pose is not a docking run. The maximum
// is NOT a scientific or server limit, and nothing downstream enforces it — a
// pose count goes straight into the `dock-compound` BFD command, and on the
// GNINA path straight to `--num_modes`. It exists only so a campaign offers the
// same range as the Docking pane, which has capped it at 10 since 2021 with no
// recorded rationale. It is taken from DEFAULT_DOCK_POSE_UPPER_BOUND — the value
// the budget already assumes when a pose count is absent — so the ceiling and
// the budget's worst-case assumption cannot drift apart.
//
// [SME] Nobody here knows where 10 came from, or what the BFD server and the
// docking programs actually tolerate. If a real limit exists, this should
// follow it instead of a form default.
export const DOCK_POSE_COUNT_MIN = 1;
export const DOCK_POSE_COUNT_MAX = DEFAULT_DOCK_POSE_UPPER_BOUND;

export const CANONICAL_HIT_TO_LEAD = Object.freeze({
    workflowId: 'hit-to-lead',
    source: 'campaign-verb-shortcut',
    // The pose count the canonical plan is generated with — the Docking pane's
    // own default, carried explicitly so neither the browser request nor the
    // audit trail can fall back on a form-only default. A reviewer may sign a
    // different value; the contract requires only that one was reviewed.
    proposedDockPoseCount: 6,
    summaryWithGrow: 'Hit-to-lead campaign: minimize every docked pose, score and filter them, grow, minimize the new products, re-score them, predict ADMET, and export.',
    summaryWithPreGrowMinimize: 'Hit-to-lead campaign: dock eligible undocked compounds, minimize every docked pose, score and filter them, grow, minimize the new products, re-score them, predict ADMET, and export.',
    summaryWithoutGrow: [
        'Hit-to-lead campaign: minimize every docked pose, score and filter them, predict ADMET,',
        'and export. Fragment growth is unavailable in this workspace.',
    ].join(' '),
    summaryWithoutGrowWithPreScoreMinimize: [
        'Hit-to-lead campaign: dock eligible undocked compounds, minimize every docked pose,',
        'score and filter them, predict ADMET, and export.',
        'Fragment growth is unavailable in this workspace.',
    ].join(' '),
    // A workspace holding a native reference alongside candidates scores the
    // native in the same step as the retained poses, so the ranked table
    // compares against a live crystal-ligand score. Appended to whichever
    // non-native summary the state selects.
    nativeReferenceScoringClause: ' The native reference is scored with the retained poses and ranked against them.',
    summaryNativeWithGrow: [
        'Native-reference hit-to-lead campaign: score the native binder, dock new poses,',
        'minimize and re-score the poses, grow plausible scored structures, minimize and',
        're-score new growth products, then predict ADMET, rank, and export ligand identities.',
    ].join(' '),
    summaryNativeWithoutGrow: [
        'Native-reference hit-to-lead campaign: score the native binder, dock new poses,',
        'and minimize and re-score the poses. Fragment growth is not authorized by current',
        'workspace evidence; continue safe ADMET, ranking, and export for the native identity.',
    ].join(' '),
});

export const CAMPAIGN_ACTION_IDS = Object.freeze([
    'dock-compound',
    'get-energies',
    'grow-from-atom',
    'energy-minimize',
    'predict-admet',
    'export-compounds',
]);

export const CAMPAIGN_AUTONOMY_MODES = Object.freeze({
    GUIDED: 'review-every-step',
    REVIEW_BRANCHES: 'review-branches-only',
    REVIEW_ESCALATIONS: 'review-escalations-only',
    FULL_AUTO: 'full-auto-within-budget',
});

export const DEFAULT_CAMPAIGN_AUTONOMY_MODE = CAMPAIGN_AUTONOMY_MODES.GUIDED;

const ALLOWED_AUTONOMY_MODES = new Set(Object.values(CAMPAIGN_AUTONOMY_MODES));

const CAMPAIGN_POLICY_BASE = Object.freeze({
    budget: Object.freeze({
        maxWallClockSec: 3600,
        maxLlmCostUsd: 5,
        forbiddenActionCategories: Object.freeze(['Workspace Management']),
        maxRetriesPerStep: 2,
        maxBranchVisits: 3,
    }),
});

const REVIEWED_GROW_CHECKPOINT_POLICY = Object.freeze({
    directedGrow: 'runtime-reviewed-vector',
    multiParent: 'sequential-parent-checkpoints',
});

const AUTONOMOUS_GROW_CHECKPOINT_POLICY = Object.freeze({
    directedGrow: 'candidate-bond-vectors',
    multiParent: 'candidate-vector-per-parent',
});

const DEFAULT_STEP_BUDGET = Object.freeze(deriveWorkflowStepBudget(
    [{ actionId: 'dock-compound', params: {} }],
    { compoundIds: ['COMPOUND_A'] },
));

export function campaignPolicyForAutonomy(
    autonomyMode = DEFAULT_CAMPAIGN_AUTONOMY_MODE,
    maxStepsDerivation = DEFAULT_STEP_BUDGET,
) {
    if (!ALLOWED_AUTONOMY_MODES.has(autonomyMode)) {
        throw new Error(`unsupported campaign autonomy mode: ${autonomyMode}`);
    }
    return {
        autonomyMode,
        humanEscalationPolicy: autonomyMode === CAMPAIGN_AUTONOMY_MODES.FULL_AUTO
            ? 'block'
            : 'pause',
        budget: {
            maxSteps: maxStepsDerivation.total,
            maxStepsDerivation,
            ...CAMPAIGN_POLICY_BASE.budget,
        },
        checkpointPolicy: autonomyMode === CAMPAIGN_AUTONOMY_MODES.FULL_AUTO
            ? AUTONOMOUS_GROW_CHECKPOINT_POLICY
            : REVIEWED_GROW_CHECKPOINT_POLICY,
    };
}

export const CAMPAIGN_POLICY = Object.freeze(campaignPolicyForAutonomy());

export function campaignGrowCheckpointState({
    hasGrow = false,
    autonomyMode = DEFAULT_CAMPAIGN_AUTONOMY_MODE,
} = {}) {
    const autonomous = autonomyMode === CAMPAIGN_AUTONOMY_MODES.FULL_AUTO;
    return {
        autonomous,
        requiredDuringExecution: Boolean(hasGrow) && !autonomous,
    };
}

function workflowIdentityForPlan(plan) {
    return {
        id: typeof plan.workflowId === 'string' ? plan.workflowId : 'workflow-checker-campaign',
        source: typeof plan.source === 'string' ? plan.source : 'workflow-checker',
        summary: typeof plan.summary === 'string'
            ? plan.summary
            : 'Reviewed campaign',
    };
}

const CAMPAIGN_ACTION_SET = new Set(CAMPAIGN_ACTION_IDS);
const NATIVE_BINDER_MUTATION_ACTION_SET = new Set(['dock-compound', 'energy-minimize']);
const MASKED_ID_RE = /^(?:COMPOUND_[A-Z]+|TARGET_X|PROTEIN_[A-Z]+)$/;
const MASKED_PROTEIN_RE = /^(?:TARGET_X|PROTEIN_[A-Z]+)$/;
const SAFE_REF_RE = /^(?:current|active|all|selected|undocked|newly-docked|docked-poses|native-and-docked-poses|grown)$/;
const UNSAFE_FIELD_RE = /(?:name|pdb|sequence|smiles|molblock|structure|workspace|protein)/i;

const NATIVE_REFERENCE_ONLY_CLASSIFICATION = Object.freeze({
    version: 1,
    kind: 'native-reference-only',
    nativeRoleSource: 'workspace-native-reference-role',
    nativeReferenceCoverage: 'all-baseline-compounds',
});

function normalizeFragmentAvailability(snapshot = {}) {
    const raw = snapshot.fragmentAvailability;
    const status = raw && ['available', 'unavailable', 'unknown'].includes(raw.status)
        ? raw.status
        : null;
    const source = raw && [
        'workspace-fragment-info',
        'saved-state-fragment-info',
        'saved-live-fragment-info',
        'legacy-fragment-count',
    ].includes(raw.source)
        ? raw.source
        : null;
    const entryCount = raw && (Number.isInteger(raw.entryCount) || raw.entryCount === null)
        ? raw.entryCount
        : undefined;
    const countAgrees = status === 'unknown'
        || !Number.isInteger(snapshot.fragInfoCount)
        || entryCount === snapshot.fragInfoCount;
    if (status && source && entryCount !== undefined && countAgrees
        && ((status === 'available' && entryCount > 0)
            || (status === 'unavailable' && entryCount === 0)
            || (status === 'unknown' && entryCount === null))) {
        return { status, source, entryCount };
    }
    if (Number.isInteger(snapshot.fragInfoCount) && snapshot.fragInfoCount >= 0) {
        return {
            status: snapshot.fragInfoCount > 0 ? 'available' : 'unavailable',
            source: 'legacy-fragment-count',
            entryCount: snapshot.fragInfoCount,
        };
    }
    return {
        status: 'unknown',
        source: 'workspace-fragment-info',
        entryCount: null,
    };
}

function cloneJson(value) {
    if (value === undefined) return undefined;
    const encoded = JSON.stringify(value);
    if (encoded === undefined) throw new Error('campaign submission contains a non-JSON value');
    return JSON.parse(encoded);
}

function hasControlCharacters(value) {
    return Array.from(value).some((character) => character.charCodeAt(0) < 32);
}

function assertSafeValue(value, field) {
    if (value == null || typeof value === 'boolean') return;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) throw new Error(`${field} must be finite`);
        return;
    }
    if (typeof value === 'string') {
        const maxLength = field === 'submission.workflow.summary'
            ? WORKFLOW_SUMMARY_MAX_LENGTH
            : 240;
        if (value.length > maxLength || hasControlCharacters(value)) {
            throw new Error(`${field} contains an unsafe string`);
        }
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((entry, index) => assertSafeValue(entry, `${field}[${index}]`));
        return;
    }
    if (typeof value === 'object') {
        Object.entries(value).forEach(([key, entry]) => {
            assertSafeValue(entry, `${field}.${key}`);
        });
        return;
    }
    throw new Error(`${field} is not JSON-safe`);
}

function sanitizeAction(action, index) {
    const actionId = action?.actionId;
    if (!CAMPAIGN_ACTION_SET.has(actionId)) {
        throw new Error(`action ${actionId || index + 1} is not campaign-capable`);
    }
    const actionDef = getAction(actionId);
    if (!actionDef) throw new Error(`unknown campaign action: ${actionId}`);
    const inputFields = new Set((actionDef.inputs || []).map((field) => field.name));
    const parameterFields = new Set((actionDef.parameters || []).map((field) => field.name));
    const fields = new Set([
        ...(actionDef.inputs || []).map((field) => field.name),
        ...(actionDef.parameters || []).map((field) => field.name),
    ]);
    const definitions = new Map([
        ...(actionDef.inputs || []),
        ...(actionDef.parameters || []),
    ].map((field) => [field.name, field]));
    const params = {};
    Object.entries(action.params || {}).forEach(([key, value]) => {
        if (!parameterFields.has(key) || !fields.has(key) || UNSAFE_FIELD_RE.test(key)) {
            throw new Error(`${actionId}.${key} is not an approved action field`);
        }
        assertSafeValue(value, `${actionId}.${key}`);
        const options = definitions.get(key)?.options;
        if (Array.isArray(options) && !options.includes(value)) {
            throw new Error(`${actionId}.${key} is not an approved option`);
        }
        params[key] = cloneJson(value);
    });
    if (actionId === 'dock-compound' && params.poseCount !== undefined
        && (!Number.isInteger(params.poseCount)
            || params.poseCount < DOCK_POSE_COUNT_MIN
            || params.poseCount > DOCK_POSE_COUNT_MAX)) {
        throw new Error(
            `dock-compound.poseCount must be an integer from ${DOCK_POSE_COUNT_MIN} to ${DOCK_POSE_COUNT_MAX}`,
        );
    }
    const inputs = {};
    Object.entries(action.resolvedInputs || {}).forEach(([key, value]) => {
        if (!inputFields.has(key)) throw new Error(`${actionId}.${key} is not an approved input`);
        if (typeof value !== 'string' || !SAFE_REF_RE.test(value)) {
            throw new Error(`${actionId}.${key} uses an unsupported workflow binding`);
        }
        inputs[key] = value;
    });
    for (const field of definitions.values()) {
        if (field.required === true && params[field.name] === undefined
            && inputs[field.name] === undefined && actionId !== 'grow-from-atom') {
            throw new Error(`${actionId}.${field.name} is required`);
        }
    }
    return {
        id: `submitted-step-${index + 1}`,
        actionId,
        params,
        ...(Object.keys(inputs).length ? { inputs } : {}),
    };
}

/**
 * Decide which structure a campaign docks into, and which compounds are in its
 * scope.
 *
 * A campaign still runs against exactly ONE docking site. What changed is how it
 * finds it: the reviewer's target/off-target role now reaches this contract, so
 * one target alongside reference structures is a workable campaign rather than a
 * blanket refusal. Docking into two sites for comparison is a reviewed scope
 * choice that does not exist yet (#26); until it does, two targets are refused.
 *
 * Docking cannot straddle structures even if we wanted it to: BMaps derives the
 * case from the compounds themselves and rejects a batch spanning more than one
 * (`ComputationCmds.getDataParentsForCompounds`). So restricting scope to the
 * target's own compounds is not a policy preference — it is what the docking
 * call accepts.
 *
 * @param {Object} args
 * @param {boolean} args.multiProtein
 * @param {{id: string, role: string}[]} args.normalizedProteins
 * @param {{id: string, proteinIndex: ?number}[]} args.compoundEntries
 * @returns {{inScope: Object[], excludedCompoundIds: string[], dockingSite: ?string}}
 */
function resolveDockingScope({ multiProtein, normalizedProteins, compoundEntries }) {
    if (!multiProtein) {
        return { inScope: compoundEntries, excludedCompoundIds: [], dockingSite: null };
    }
    const targets = normalizedProteins.filter((protein) => protein.role === 'target');
    if (targets.length > 1) {
        const error = new Error(
            `a campaign docks into one structure, but ${targets.length} of the `
            + `${normalizedProteins.length} structures in this workspace are marked as targets`,
        );
        error.campaignBlocker = MULTI_PROTEIN_WORKSPACE;
        throw error;
    }
    if (targets.length === 0) {
        const error = new Error(
            `this workspace has ${normalizedProteins.length} structures and none of them `
            + 'is marked as the target',
        );
        error.campaignBlocker = NO_TARGET_PROTEIN;
        throw error;
    }
    const [target] = targets;
    const targetIndex = normalizedProteins.indexOf(target) + 1;
    // A compound with no attribution cannot be placed under any structure, so it
    // can be neither docked nor excluded on the reviewer's behalf. Refusing is
    // the only honest option: silently dropping it would lose a candidate, and
    // silently keeping it would send docking a batch BMaps rejects.
    const unattributed = compoundEntries.filter((entry) => !entry.proteinIndex);
    if (unattributed.length > 0) {
        const error = new Error(
            `${unattributed.length} of the ${compoundEntries.length} compounds in this `
            + 'workspace could not be attributed to one of its structures',
        );
        error.campaignBlocker = UNATTRIBUTED_COMPOUND;
        throw error;
    }
    const inScope = compoundEntries.filter((entry) => entry.proteinIndex === targetIndex);
    const excludedCompoundIds = compoundEntries
        .filter((entry) => entry.proteinIndex !== targetIndex)
        .map((entry) => entry.id);
    return { inScope, excludedCompoundIds, dockingSite: target.id };
}

export function normalizeWorkspaceBaseline(snapshot = {}) {
    const proteins = Array.isArray(snapshot.proteins) ? snapshot.proteins : [];
    const compounds = Array.isArray(snapshot.compounds) ? snapshot.compounds : [];
    const workspaceBindingId = snapshot.meta?.workspaceBindingId || snapshot.workspaceBindingId;
    if (typeof workspaceBindingId !== 'string' || !/^WS_[A-Za-z0-9_-]{12,}$/.test(workspaceBindingId)) {
        throw new Error('campaign workspace is missing its opaque binding');
    }
    const fragmentAvailability = normalizeFragmentAvailability(snapshot);
    // Everything role-aware below is conditioned on this. A single-protein
    // workspace normalizes exactly as it did before roles existed — same ids,
    // same `role: 'protein'`, no new fields — so the seven signed narratives
    // keep their digests and need no regeneration.
    const multiProtein = proteins.length > 1;
    const normalizedProteins = proteins.map((protein, index) => {
        const positional = `PROTEIN_${indexToken(index)}`;
        const id = protein?.id
            || (!multiProtein && protein?.isTarget ? 'TARGET_X' : positional);
        if (!MASKED_PROTEIN_RE.test(id)) throw new Error('campaign protein identity is not masked');
        if (!multiProtein) {
            return { id, role: protein?.isTarget === true ? 'target' : 'protein' };
        }
        // 'unknown' is the honest value when nothing set a role — including when
        // the role reader is unwired — and the guards below refuse it rather
        // than picking a docking site on the reviewer's behalf.
        return { id, role: protein?.role || (protein?.isTarget === true ? 'target' : 'unknown') };
    });
    // Masked identity per compound, with the protein it sits under. Derived over
    // the unfiltered list so a compound's masked id never depends on which
    // compounds are in scope.
    const compoundEntries = compounds.map((compound, index) => {
        const id = compound?.id || `COMPOUND_${indexToken(index)}`;
        if (!MASKED_ID_RE.test(id)) throw new Error('campaign compound identity is not masked');
        const proteinIndex = Number.isInteger(compound?.proteinIndex)
            ? compound.proteinIndex
            : null;
        return { id, proteinIndex, compound };
    });
    const proteinIds = normalizedProteins.map((protein) => protein.id);
    if (new Set(proteinIds).size !== normalizedProteins.length) {
        throw new Error('campaign protein identities are not unique');
    }
    const dockingScope = resolveDockingScope({
        multiProtein,
        normalizedProteins,
        compoundEntries,
    });
    const normalized = {
        version: 1,
        workspaceBindingId,
        proteins: normalizedProteins,
        compoundIds: dockingScope.inScope.map((entry) => entry.id),
        fragInfoCount: fragmentAvailability.entryCount || 0,
        fragmentAvailability,
    };
    if (dockingScope.dockingSite) {
        normalized.dockingSite = dockingScope.dockingSite;
    }
    if (dockingScope.excludedCompoundIds.length > 0) {
        normalized.offTargetCompoundIds = dockingScope.excludedCompoundIds;
    }
    const inScope = dockingScope.inScope;
    const nativeReferenceIds = inScope
        .filter((entry) => entry.compound?.isNativeBinder === true)
        .map((entry) => entry.id);
    const initialDockingScoreIds = inScope
        .filter((entry) => Number.isFinite(entry.compound?.affinity)
            || Number.isFinite(entry.compound?.pose?.score))
        .map((entry) => entry.id);
    const initialDockedPoseIds = inScope
        .filter((entry) => entry.compound?.isDockedPose === true)
        .map((entry) => entry.id);
    if (nativeReferenceIds.length > 0) normalized.nativeReferenceIds = nativeReferenceIds;
    if (initialDockingScoreIds.length > 0) {
        normalized.initialDockingScoreIds = initialDockingScoreIds;
    }
    if (initialDockedPoseIds.length > 0) {
        normalized.initialDockedPoseIds = initialDockedPoseIds;
    }
    if (normalized.fragInfoCount < 0) throw new Error('campaign fragment-map count is invalid');
    if (new Set(normalized.compoundIds).size !== normalized.compoundIds.length) {
        throw new Error('campaign compound identities are not unique');
    }
    if (new Set(nativeReferenceIds).size !== nativeReferenceIds.length
        || nativeReferenceIds.some((id) => !normalized.compoundIds.includes(id))) {
        throw new Error('campaign native-binder identities are invalid');
    }
    if (new Set(initialDockingScoreIds).size !== initialDockingScoreIds.length
        || initialDockingScoreIds.some((id) => !normalized.compoundIds.includes(id))) {
        throw new Error('campaign initial docking-score identities are invalid');
    }
    if (new Set(initialDockedPoseIds).size !== initialDockedPoseIds.length
        || initialDockedPoseIds.some((id) => !normalized.compoundIds.includes(id))) {
        throw new Error('campaign initial docked-pose identities are invalid');
    }
    const nativeReferenceOnly = normalized.compoundIds.length > 0
        && nativeReferenceIds.length === normalized.compoundIds.length;
    if (nativeReferenceOnly) {
        // This is a presentation-safe explanation of the browser's existing
        // classification rule. Native-reference identities remain in the
        // masked nativeReferenceIds list; no lineage labels, names, or
        // structures cross the signed submission boundary.
        normalized.startingStateClassification = {
            ...NATIVE_REFERENCE_ONLY_CLASSIFICATION,
            compoundCount: normalized.compoundIds.length,
            nativeReferenceCount: nativeReferenceIds.length,
            fragmentAvailabilityStatus: normalized.fragmentAvailability.status,
            fragmentMapEntryCount: normalized.fragmentAvailability.entryCount,
        };
    }
    return normalized;
}

export function workspaceSnapshotMatchesBaseline(snapshot, workspaceBaseline) {
    if (!workspaceBaseline || typeof workspaceBaseline !== 'object') return false;
    return JSON.stringify(normalizeWorkspaceBaseline(snapshot))
        === JSON.stringify(workspaceBaseline);
}

function rejectPreSubmittedGrowVector(vector) {
    if (vector !== undefined && vector !== null) {
        throw new Error(
            'grow directions are reviewed during execution at the parent-specific checkpoint',
        );
    }
}

function validateCanonicalHitToLead(plan, steps, workspaceBaseline) {
    const hasCanonicalId = plan.workflowId === CANONICAL_HIT_TO_LEAD.workflowId;
    const hasCanonicalSource = plan.source === CANONICAL_HIT_TO_LEAD.source;
    if (hasCanonicalId && !hasCanonicalSource) {
        throw new Error('Hit-to-lead workflow identity is incomplete. Regenerate and review it before submitting.');
    }
    const isCanonical = hasCanonicalId && hasCanonicalSource;
    if (!isCanonical) return;

    const growSteps = steps.filter((step) => step.actionId === 'grow-from-atom');
    const nativeOnly = workspaceBaseline.startingStateClassification?.kind
        === NATIVE_REFERENCE_ONLY_CLASSIFICATION.kind;
    const growAvailable = workspaceBaseline.fragmentAvailability.status === 'available';
    const nativeReferenceIds = new Set(workspaceBaseline.nativeReferenceIds || []);
    const initialDockedPoseIds = new Set(workspaceBaseline.initialDockedPoseIds || []);
    const candidateIds = workspaceBaseline.compoundIds.filter(
        (id) => !nativeReferenceIds.has(id),
    );
    const hasUndockedCandidates = candidateIds.some((id) => !initialDockedPoseIds.has(id));
    const scoresNativeReference = !nativeOnly && nativeReferenceIds.size > 0;
    const poseScoreRef = scoresNativeReference ? 'native-and-docked-poses' : 'docked-poses';
    let expectedActions;
    let expectedSummary;
    let expectedCompoundRefs;
    if (nativeOnly) {
        expectedActions = [
            'get-energies', 'dock-compound', 'energy-minimize', 'get-energies',
            ...(growAvailable
                ? ['grow-from-atom', 'energy-minimize', 'get-energies']
                : []),
            'predict-admet', 'export-compounds',
        ];
        expectedSummary = growAvailable
            ? CANONICAL_HIT_TO_LEAD.summaryNativeWithGrow
            : CANONICAL_HIT_TO_LEAD.summaryNativeWithoutGrow;
        expectedCompoundRefs = expectedActions.map((actionId) => (
            actionId === 'grow-from-atom' ? null : 'all'
        ));
    } else if (growAvailable && hasUndockedCandidates) {
        expectedActions = [
            'dock-compound', 'energy-minimize', 'get-energies', 'grow-from-atom',
            'energy-minimize', 'get-energies', 'predict-admet', 'export-compounds',
        ];
        expectedSummary = CANONICAL_HIT_TO_LEAD.summaryWithPreGrowMinimize;
        expectedCompoundRefs = [
            'undocked', 'docked-poses', poseScoreRef, null,
            'grown', 'grown', 'all', 'all',
        ];
    } else if (growAvailable) {
        expectedActions = [
            'energy-minimize', 'get-energies', 'grow-from-atom',
            'energy-minimize', 'get-energies', 'predict-admet', 'export-compounds',
        ];
        expectedSummary = CANONICAL_HIT_TO_LEAD.summaryWithGrow;
        expectedCompoundRefs = [
            'docked-poses', poseScoreRef, null,
            'grown', 'grown', 'all', 'all',
        ];
    } else if (hasUndockedCandidates) {
        expectedActions = [
            'dock-compound', 'energy-minimize', 'get-energies',
            'predict-admet', 'export-compounds',
        ];
        expectedSummary = CANONICAL_HIT_TO_LEAD.summaryWithoutGrowWithPreScoreMinimize;
        expectedCompoundRefs = ['undocked', 'docked-poses', poseScoreRef, 'all', 'all'];
    } else {
        expectedActions = [
            'energy-minimize', 'get-energies',
            'predict-admet', 'export-compounds',
        ];
        expectedSummary = CANONICAL_HIT_TO_LEAD.summaryWithoutGrow;
        expectedCompoundRefs = ['docked-poses', poseScoreRef, 'all', 'all'];
    }
    if (scoresNativeReference) {
        expectedSummary += CANONICAL_HIT_TO_LEAD.nativeReferenceScoringClause;
    }
    if ((growAvailable && growSteps.length !== 1) || (!growAvailable && growSteps.length !== 0)) {
        throw new Error(
            'Fragment availability changed after this hit-to-lead workflow was reviewed. '
            + 'Regenerate and review the workflow before submitting.',
        );
    }
    if (steps.map((step) => step.actionId).join('|') !== expectedActions.join('|')) {
        throw new Error('Canonical hit-to-lead steps do not match the reviewed workflow. Regenerate and review it before submitting.');
    }
    if (steps.some((step, index) => (
        (step.inputs?.compounds || null) !== expectedCompoundRefs[index]
    ))) {
        throw new Error('Canonical hit-to-lead compound scopes do not match the reviewed workspace state. Regenerate and review it before submitting.');
    }
    if (plan.summary !== expectedSummary) {
        throw new Error('Canonical hit-to-lead summary does not match the reviewed workflow. Regenerate and review it before submitting.');
    }
    // Requires an explicitly reviewed pose count, not one specific value. The
    // reviewer may raise or lower it within the supported range; the signed
    // step budget is derived from the value they approve, which is what
    // `signed-reviewed-dock-pose-upper-bound` records.
    const dockStep = steps.find((step) => step.actionId === 'dock-compound');
    if (nativeOnly || hasUndockedCandidates) {
        const reviewedPoseCount = dockStep?.params.poseCount;
        if (!Number.isInteger(reviewedPoseCount)
            || reviewedPoseCount < DOCK_POSE_COUNT_MIN
            || reviewedPoseCount > DOCK_POSE_COUNT_MAX) {
            throw new Error(
                'Canonical hit-to-lead docking must carry an explicitly reviewed pose count '
                + `from ${DOCK_POSE_COUNT_MIN} to ${DOCK_POSE_COUNT_MAX}.`,
            );
        }
    }
    if (growSteps.length === 1
        && !['fragment-best-pose', 'fragment-all-poses'].includes(growSteps[0].params.growMethodology)) {
        throw new Error('Canonical hit-to-lead grow methodology must be explicitly reviewed.');
    }
}

function rejectDynamicNativeBinderMutationScope(steps, workspaceBaseline) {
    const nativeReferenceIds = Array.isArray(workspaceBaseline.nativeReferenceIds)
        ? workspaceBaseline.nativeReferenceIds
        : [];
    if (nativeReferenceIds.length === 0) return;
    const unsafeStep = steps.find((step) => (
        NATIVE_BINDER_MUTATION_ACTION_SET.has(step.actionId)
        && ['selected', 'active'].includes(step.inputs?.compounds)
    ));
    if (unsafeStep) {
        throw new Error(
            `${unsafeStep.actionId} cannot use a dynamic selected/active scope when a native reference is present`,
        );
    }
}

export function prepareWorkflowSubmission({ plan, workspace } = {}) {
    if (!plan || plan.executionMode !== 'campaign' || !Array.isArray(plan.actions)
        || plan.actions.length === 0) {
        throw new Error('only an explicit campaign plan can be submitted');
    }
    const steps = plan.actions.map(sanitizeAction);
    const workspaceBaseline = normalizeWorkspaceBaseline(workspace);
    rejectDynamicNativeBinderMutationScope(steps, workspaceBaseline);
    validateCanonicalHitToLead(plan, steps, workspaceBaseline);
    if (steps.some((step) => step.actionId === 'grow-from-atom')
        && workspaceBaseline.compoundIds.length === 0) {
        throw new Error('grow campaign requires at least one reviewed baseline parent');
    }
    const workflow = workflowIdentityForPlan(plan);
    const maxStepsDerivation = deriveWorkflowStepBudget(steps, workspaceBaseline, workflow);
    return {
        workflow,
        steps,
        workspaceBaseline,
        maxStepsDerivation,
    };
}

export async function buildWorkflowSubmission({
    plan,
    workspace,
    reviewedGrowVector,
    autonomyMode = DEFAULT_CAMPAIGN_AUTONOMY_MODE,
} = {}) {
    const {
        workflow,
        steps,
        workspaceBaseline,
        maxStepsDerivation,
    } = prepareWorkflowSubmission({ plan, workspace });
    const policy = campaignPolicyForAutonomy(autonomyMode, maxStepsDerivation);
    // Parent identity is only authoritative when execution reaches the grow step.
    // Human-in-the-loop modes therefore collect vectors at the runtime checkpoint;
    // full-auto resolves bounded candidates in the live bridge. Neither path accepts
    // a stale vector captured before submission.
    rejectPreSubmittedGrowVector(reviewedGrowVector);
    const body = {
        version: WORKFLOW_SUBMISSION_VERSION,
        workflow,
        executionMode: 'campaign',
        steps,
        policy: cloneJson(policy),
        workspaceBaseline,
    };
    assertSafeValue(body, 'submission');
    return { ...body, submissionDigest: await signRecord(body) };
}

export function isCampaignPlan(plan) {
    return plan?.executionMode === 'campaign';
}

export function campaignDigestAfterSubmitFailure(activeDigest, failedDigest) {
    return failedDigest && activeDigest === failedDigest ? null : activeDigest;
}
